import dynamic from "next/dynamic";
import Link from "next/link";

import React, { useCallback, useEffect, useMemo, useState } from "react";

import addMonths from "date-fns/addMonths";
import startOfDay from "date-fns/startOfDay";
import numeral from "numeral";
import { twMerge } from "tailwind-merge";
import { useTimeout } from "usehooks-ts";

import { faCaretDown } from "@fortawesome/free-solid-svg-icons/faCaretDown";
import { faCaretUp } from "@fortawesome/free-solid-svg-icons/faCaretUp";

import { SKSpinner } from "components/customElements/skSpinner";
import { entityUrl } from "components/EntityPageLayout";
import { currentStatsURL } from "components/EntityStatsCard";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import { useApi } from "hooks/useApi";
import useMediaQuery from "hooks/useMediaQuery";
import { usePriceQuote } from "hooks/usePriceQuote";
import { baseFetcher } from "utils/baseFetcher";

import styles from "./style.module.scss";

/* eslint-disable unused-imports/no-unused-vars */
export enum TimeUnit {
  Day = "d1",
  Quarter = "m3",
  Year = "y1",
  Year5 = "y5",
}
/* eslint-enable unused-imports/no-unused-vars */

const TIME_UNIT_LABELS = {
  [TimeUnit.Day]: "1D",
  [TimeUnit.Quarter]: "3M",
  [TimeUnit.Year]: "1Y",
  [TimeUnit.Year5]: "5Y",
};

const Chart = dynamic(() => import("components/EntityAnalyticsChart/Chart"), {
  ssr: false,
});

export const entityChartUrl = (entity: any, timeUnit: TimeUnit) => {
  if (!entity.security && !entity.yahooTicker) return null;

  return `/api/v2/price-api/get-chart?ticker=${entity.security}&yahoo_ticker=${
    entity.yahooTicker
  }&interval=${timeUnit !== TimeUnit.Quarter ? timeUnit : TimeUnit.Year}`;
};

const EntityAnalyticsChart = ({
  slug,
  onEmpty,
  showTitle,
  titleClassName,
  priceClassName,
  ...props
}: any) => {
  const { mobile } = useMediaQuery();

  const [timeUnit, setTimeUnit] = useState(TimeUnit.Year);
  const [showChart, setShowChart] = useState(false);

  const { data: entityData } = useApi(
    slug && !props.entity ? entityUrl(slug) : null,
    baseFetcher
  );
  const entity = props.entity || entityData;

  const { data: chartRawData, isValidating: isChartLoading } = useApi(
    entity && entityChartUrl(entity, timeUnit),
    baseFetcher
  );

  useEffect(() => {
    if (!isChartLoading && chartRawData?.error) {
      onEmpty && onEmpty();
    }
  }, [chartRawData, isChartLoading, onEmpty]);

  const { close, time_period } = chartRawData || {};

  const chartData = useMemo(() => {
    const data =
      close?.map?.((value: number, index: number) => ({
        value,
        date: new Date(time_period[index]).getTime(),
      })) || [];
    if (timeUnit === TimeUnit.Quarter) {
      return data.filter(
        (t: any) => t.date >= startOfDay(addMonths(new Date(), -3)).getTime()
      );
    }
    return data;
  }, [close, timeUnit, time_period]);

  const {
    price,
    percentage,
    currency: priceQuoteCurrency,
    isLoading: isPriceLoading,
  } = usePriceQuote(entity?.security);

  const { data: currentStats } = useApi(
    isPriceLoading || priceQuoteCurrency || !entity?.isin
      ? null
      : currentStatsURL(entity?.isin),
    baseFetcher
  );

  const currency = priceQuoteCurrency || currentStats?.currency;

  const priceUnitClassName =
    percentage &&
    (percentage < 0 ? styles.priceUnitDecrease : styles.priceUnitIncrease);

  const onTimeUnitChange = useCallback((timeUnit: TimeUnit) => {
    setTimeUnit(timeUnit);
  }, []);

  useTimeout(() => setShowChart(true), 0);

  return (
    <>
      {showTitle && (
        <div className="mx-[10px] mb-[10px]">
          <Link
            href={`/entities/${entity?.slug}/research`}
            className={twMerge(
              "text-[14px] hover:text-smart-teal",
              titleClassName
            )}
          >
            {entity?.fulltext}
          </Link>
        </div>
      )}
      <div className={styles.container}>
        <div className={styles.zoom}>
          {Object.values(TimeUnit).map((tu) => (
            <button
              key={tu}
              className={`${
                timeUnit === tu ? styles.active : "text-active-black"
              }`}
              onClick={() => onTimeUnitChange(tu)}
            >
              {TIME_UNIT_LABELS[tu]}
            </button>
          ))}
        </div>

        {!mobile &&
          (isPriceLoading ? (
            <div className={styles.priceSpinner}>
              <SKSpinner />
            </div>
          ) : (
            typeof price !== "undefined" &&
            typeof percentage !== "undefined" && (
              <div className={twMerge(priceUnitClassName, priceClassName)}>
                <span>{currency}</span>
                <span>{numeral(price).format("0,0[.]00")}</span>
                {(percentage as number) < 0 ? (
                  <FontAwesomeIcon icon={faCaretDown} size={"lg"} />
                ) : (
                  <FontAwesomeIcon icon={faCaretUp} size={"lg"} />
                )}
                <span>{Math.abs(percentage).toFixed(2)}%</span>
              </div>
            )
          ))}
      </div>

      <div className={twMerge(styles.chartContainer, styles.mobileLayout)}>
        {isChartLoading || !showChart ? (
          <div className={twMerge(styles.chartSpinner, styles.mobileLayout)}>
            <SKSpinner />
          </div>
        ) : (
          <Chart
            key={`${timeUnit}`}
            chartData={chartData}
            timeUnit={timeUnit}
            tooltipUnit={currency}
          />
        )}
      </div>
    </>
  );
};

export default EntityAnalyticsChart;
